/**
 * Resize an image file to a maximum width/height while maintaining aspect ratio
 * and preserving quality.
 */
export const resizeImage = async (
  file: File,
  maxWidth = 1200,
  maxHeight = 1200,
  quality = 0.85
): Promise<Blob> => {
  const imageUrl = URL.createObjectURL(file);

  try {
    const canvas = await loadImageToCanvas(imageUrl);

    let { width, height } = canvas;

    // Only downscale, never upscale
    if (width <= maxWidth && height <= maxHeight) {
      // Return original file as-is if already small enough
      return file;
    }

    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    width = Math.round(width);
    height = Math.round(height);

    const resizedCanvas = document.createElement("canvas");
    resizedCanvas.width = width;
    resizedCanvas.height = height;

    const ctx = resizedCanvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0, width, height);

    return new Promise((resolve, reject) => {
      resizedCanvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Blob creation failed"))),
        "image/jpeg",
        quality
      );
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

const loadImageToCanvas = (imageUrl: string): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
    img.src = imageUrl;
  });
};
